'use client';

import React, { useState, useMemo, useRef } from 'react';
import { Search, Plus, Tag, Grid, List, Edit2, Trash2, Package, ChevronDown, X, Calculator, Printer, Key } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import Modal from '@/components/ui/Modal';

import ProductForm from './ProductForm';
import CatalogAccessCodesPanel from './CatalogAccessCodesPanel';
import { toast } from 'sonner';

// Mock data — backend: SELECT * FROM unified_products_catalog WHERE supplier_id = auth.uid()
const allProducts = [
{ id: 'prod-001', name: 'Fromage Frais Kiri 500g', category: 'Dairy', barcode: '6191000101107', wholesale_price: 28.50, retail_price: 35.00, tax_rate: 0.10, unit_type: 'Piece', has_active_discount: true, discount_pct: 12, stock_status: 'in_stock', image: "https://images.unsplash.com/photo-1635407862451-58962c048a76", alt: 'Fresh white cheese wheel on wooden board' },
{ id: 'prod-002', name: 'Tomates Fraîches Calibre A', category: 'Vegetables', barcode: '6191000201204', wholesale_price: 4.20, retail_price: 6.50, tax_rate: 0.00, unit_type: 'Kilogram', has_active_discount: false, discount_pct: 0, stock_status: 'in_stock', image: "https://images.unsplash.com/photo-1693946565918-72325e27e4af", alt: 'Red ripe tomatoes in a crate' },
{ id: 'prod-003', name: 'Poulet Entier Halal 1.2kg', category: 'Meats', barcode: '6191000301301', wholesale_price: 32.00, retail_price: 42.00, tax_rate: 0.10, unit_type: 'Piece', has_active_discount: true, discount_pct: 8, stock_status: 'low_stock', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f474d4d9-1773092220648.png", alt: 'Whole raw chicken on white background' },
{ id: 'prod-004', name: 'Pizza Margherita Surgelée', category: 'Pizzas', barcode: '6191000401408', wholesale_price: 18.00, retail_price: 25.00, tax_rate: 0.20, unit_type: 'Piece', has_active_discount: false, discount_pct: 0, stock_status: 'in_stock', image: "https://images.unsplash.com/photo-1536336683288-b57873dca539", alt: 'Margherita pizza with tomato sauce and mozzarella' },
{ id: 'prod-005', name: 'Lait Entier Centrale 1L', category: 'Dairy', barcode: '6191000501505', wholesale_price: 7.80, retail_price: 9.50, tax_rate: 0.07, unit_type: 'Piece', has_active_discount: false, discount_pct: 0, stock_status: 'in_stock', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f67bca5b-1766529777771.png", alt: 'White milk carton on clean background' },
{ id: 'prod-006', name: 'Carottes Bio Botte 1kg', category: 'Vegetables', barcode: '6191000601602', wholesale_price: 3.50, retail_price: 5.50, tax_rate: 0.00, unit_type: 'Kilogram', has_active_discount: true, discount_pct: 15, stock_status: 'in_stock', image: "https://images.unsplash.com/photo-1602496875335-b0fc93069cf9", alt: 'Fresh orange carrots with green tops on wood' },
{ id: 'prod-007', name: 'Viande Hachée Bœuf 500g', category: 'Meats', barcode: '6191000701709', wholesale_price: 38.00, retail_price: 48.00, tax_rate: 0.10, unit_type: 'Piece', has_active_discount: false, discount_pct: 0, stock_status: 'out_of_stock', image: "https://images.unsplash.com/photo-1689760661349-0bc890d3ccee", alt: 'Raw ground beef in tray' },
{ id: 'prod-008', name: 'Pizza 4 Fromages Format Pro', category: 'Pizzas', barcode: '6191000801806', wholesale_price: 22.00, retail_price: 32.00, tax_rate: 0.20, unit_type: 'Piece', has_active_discount: true, discount_pct: 5, stock_status: 'in_stock', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1933c31e9-1772650700538.png", alt: 'Four cheese pizza with golden crust' },
{ id: 'prod-009', name: 'Yaourt Nature Danone 4×125g', category: 'Dairy', barcode: '6191000901903', wholesale_price: 12.50, retail_price: 16.00, tax_rate: 0.07, unit_type: 'Carton_Box', has_active_discount: false, discount_pct: 0, stock_status: 'in_stock', image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b73677d-1768471097939.png", alt: 'White yogurt cups in packaging' },
{ id: 'prod-010', name: 'Oignons Rouges Sac 5kg', category: 'Vegetables', barcode: '6191001002000', wholesale_price: 8.00, retail_price: 13.00, tax_rate: 0.00, unit_type: 'Carton_Box', has_active_discount: false, discount_pct: 0, stock_status: 'in_stock', image: "https://images.unsplash.com/photo-1680043026785-ddee499f03a5", alt: 'Red onions in a pile on wooden surface' },
{ id: 'prod-011', name: 'Escalope Dinde Halal 1kg', category: 'Meats', barcode: '6191001102107', wholesale_price: 45.00, retail_price: 58.00, tax_rate: 0.10, unit_type: 'Kilogram', has_active_discount: true, discount_pct: 10, stock_status: 'low_stock', image: "https://images.unsplash.com/photo-1604503468506-a8da13d82791", alt: 'Raw turkey escalope slices on white background' },
{ id: 'prod-012', name: 'Beurre Paysan 250g', category: 'Dairy', barcode: '6191001202204', wholesale_price: 14.00, retail_price: 18.50, tax_rate: 0.07, unit_type: 'Piece', has_active_discount: false, discount_pct: 0, stock_status: 'in_stock', image: "https://img.rocket.new/generatedImages/rocket_gen_img_156571d2c-1770258068384.png", alt: 'Butter block wrapped in paper on marble surface' }];


const categories = ['Tous', 'Dairy', 'Vegetables', 'Meats', 'Pizzas'];
const taxRates = ['Tous', '0%', '7%', '10%', '20%'];

const categoryLabels: Record<string, string> = {
  Dairy: 'Produits Laitiers',
  Vegetables: 'Légumes',
  Meats: 'Viandes',
  Pizzas: 'Pizzas'
};

const stockStatusConfig: Record<string, {label: string;className: string;}> = {
  in_stock: { label: 'En Stock', className: 'bg-success-light text-success' },
  low_stock: { label: 'Stock Faible', className: 'bg-warning-light text-warning' },
  out_of_stock: { label: 'Rupture', className: 'bg-danger-light text-danger' }
};

// ─── EAN-13 helpers ───────────────────────────────────────────────────────────
function generateEAN13(): string {
  const prefix = '619';
  let body = '';
  for (let i = 0; i < 9; i++) body += Math.floor(Math.random() * 10).toString();
  const digits = (prefix + body).split('').map(Number);
  let sum = 0;
  digits.forEach((d, i) => {sum += i % 2 === 0 ? d : d * 3;});
  const check = (10 - sum % 10) % 10;
  return prefix + body + check.toString();
}

function BarcodeDisplay({ value, width = 160, height = 50 }: {value: string;width?: number;height?: number;}) {
  const L = ['0001101', '0011001', '0010011', '0111101', '0100011', '0110001', '0101111', '0111011', '0110111', '0001011'];
  const G = ['0100111', '0110011', '0011011', '0100001', '0011101', '0111001', '0000101', '0010001', '0001001', '0010111'];
  const R = ['1110010', '1100110', '1101100', '1000010', '1011100', '1001110', '1010000', '1000100', '1001000', '1110100'];
  const parityTable = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG', 'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL'];

  if (value.length !== 13) return <div className="text-xs text-muted-foreground font-mono">{value}</div>;

  const digits = value.split('').map(Number);
  const firstDigit = digits[0];
  const parity = parityTable[firstDigit];

  let bars = '101';
  for (let i = 0; i < 6; i++) bars += parity[i] === 'L' ? L[digits[i + 1]] : G[digits[i + 1]];
  bars += '01010';
  for (let i = 7; i < 13; i++) bars += R[digits[i]];
  bars += '101';

  const barWidth = width / bars.length;
  const rects: React.ReactNode[] = [];
  bars.split('').forEach((b, i) => {
    if (b === '1') rects.push(<rect key={i} x={i * barWidth} y={0} width={barWidth} height={height} fill="#000" />);
  });

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
      {rects}
    </svg>);

}

// ─── Print Label Modal ────────────────────────────────────────────────────────
interface PrintLabelProduct {
  name: string;
  category: string;
  wholesale_price: number;
  retail_price: number;
  barcode: string;
  unit_type: string;
  tax_rate: number;
}

function PrintLabelModal({ product, onClose }: {product: PrintLabelProduct;onClose: () => void;}) {
  const printRef = useRef<HTMLDivElement>(null);
  const [copies, setCopies] = useState(1);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=420,height=320');
    if (!printWindow) return;
    const labelsHtml = Array(copies).fill(content.innerHTML).join('');
    printWindow.document.write(`
      <html><head><title>Impression Étiquette</title>
      <style>
        body { margin: 0; padding: 8px; font-family: Arial, sans-serif; background: white; }
        .label { border: 1px solid #ccc; border-radius: 6px; padding: 8px 10px; width: 200px; display: inline-block; margin: 4px; text-align: center; vertical-align: top; }
        .label-name { font-size: 11px; font-weight: bold; color: #111; margin-bottom: 2px; }
        .label-cat { font-size: 9px; color: #666; margin-bottom: 4px; }
        .label-price { font-size: 13px; font-weight: bold; color: #2563eb; margin-bottom: 2px; }
        .label-retail { font-size: 9px; color: #888; margin-bottom: 4px; }
        .label-barcode { margin: 4px 0; }
        .label-code { font-size: 8px; font-family: monospace; color: #333; letter-spacing: 1px; }
        @media print { body { padding: 0; } }
      </style></head>
      <body>${labelsHtml}</body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {printWindow.print();printWindow.close();}, 300);
  };

  const unitLabel = product.unit_type === 'Carton_Box' ? 'Carton' : product.unit_type === 'Kilogram' ? 'kg' : 'Pièce';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Printer size={15} className="text-blue-600" />
            </div>
            <h3 className="font-bold text-sm text-foreground">Imprimer Étiquette Produit</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>

        {/* Label preview */}
        <div className="flex justify-center mb-5">
          <div ref={printRef} className="label border border-border rounded-lg p-3 w-52 text-center bg-white">
            <p className="label-name text-xs font-bold text-gray-900 mb-0.5 truncate">{product.name}</p>
            <p className="label-cat text-xs text-gray-500 mb-1">{categoryLabels[product.category] || product.category} · {unitLabel}</p>
            <p className="label-price text-sm font-bold text-blue-600 mb-0.5">
              Gros: {product.wholesale_price.toFixed(2)} MAD
            </p>
            <p className="label-retail text-xs text-gray-500 mb-2">
              Détail: {product.retail_price.toFixed(2)} MAD · TVA {(product.tax_rate * 100).toFixed(0)}%
            </p>
            <div className="label-barcode flex justify-center mb-1">
              <BarcodeDisplay value={product.barcode} width={160} height={40} />
            </div>
            <p className="label-code text-xs font-mono text-gray-600 tracking-widest">{product.barcode}</p>
          </div>
        </div>

        {/* Copies */}
        <div className="flex items-center justify-between mb-4 px-1">
          <span className="text-xs text-muted-foreground">Nombre de copies</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setCopies((c) => Math.max(1, c - 1))} className="w-7 h-7 rounded-lg border border-border flex items-center justify-center text-sm hover:bg-muted">−</button>
            <span className="text-sm font-bold w-6 text-center">{copies}</span>
            <button onClick={() => setCopies((c) => Math.min(20, c + 1))} className="w-7 h-7 rounded-lg border border-border flex items-center justify-center text-sm hover:bg-muted">+</button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted">Annuler</button>
          <button onClick={handlePrint} className="py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 flex items-center justify-center gap-1.5">
            <Printer size={14} /> Imprimer
          </button>
        </div>
      </div>
    </div>);

}

// ─── Calculator Widget ────────────────────────────────────────────────────────
function CatalogCalculator({ onClose }: {onClose: () => void;}) {
  const [display, setDisplay] = useState('0');
  const [prev, setPrev] = useState<string | null>(null);
  const [op, setOp] = useState<string | null>(null);
  const [waitNext, setWaitNext] = useState(false);

  const handleDigit = (d: string) => {
    if (waitNext) {setDisplay(d);setWaitNext(false);return;}
    setDisplay(display === '0' ? d : display + d);
  };
  const handleDot = () => {
    if (waitNext) {setDisplay('0.');setWaitNext(false);return;}
    if (!display.includes('.')) setDisplay(display + '.');
  };
  const handleOp = (o: string) => {setPrev(display);setOp(o);setWaitNext(true);};
  const handleEquals = () => {
    if (!op || !prev) return;
    const a = parseFloat(prev),b = parseFloat(display);
    let result = 0;
    if (op === '+') result = a + b;else
    if (op === '-') result = a - b;else
    if (op === '×') result = a * b;else
    if (op === '÷') result = b !== 0 ? a / b : 0;
    setDisplay(parseFloat(result.toFixed(4)).toString());
    setPrev(null);setOp(null);setWaitNext(true);
  };
  const handleClear = () => {setDisplay('0');setPrev(null);setOp(null);setWaitNext(false);};
  const handleBackspace = () => {if (waitNext) return;setDisplay(display.length > 1 ? display.slice(0, -1) : '0');};
  const handlePercent = () => {setDisplay((parseFloat(display) / 100).toString());};

  const btn = (label: string, onClick: () => void, cls = '') =>
  <button key={label} onClick={onClick} className={`h-10 rounded-xl text-sm font-semibold transition-colors active:scale-95 ${cls}`}>{label}</button>;


  return (
    <div className="bg-card border border-border rounded-2xl shadow-xl overflow-hidden w-64">
      <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b border-border">
        <div className="flex items-center gap-1.5">
          <Calculator size={13} className="text-primary" />
          <span className="text-xs font-semibold text-foreground">Calculatrice</span>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-0.5"><X size={14} /></button>
      </div>
      <div className="px-3 py-2 bg-gray-900 text-right">
        {op && prev && <p className="text-xs text-gray-400 font-mono truncate">{prev} {op}</p>}
        <p className="text-2xl font-bold text-white font-mono truncate">{display}</p>
      </div>
      <div className="p-2 grid grid-cols-4 gap-1.5">
        {btn('AC', handleClear, 'bg-red-100 text-red-700 hover:bg-red-200')}
        {btn('⌫', handleBackspace, 'bg-amber-100 text-amber-700 hover:bg-amber-200')}
        {btn('%', handlePercent, 'bg-muted text-muted-foreground hover:bg-muted/80')}
        {btn('÷', () => handleOp('÷'), `${op === '÷' ? 'bg-primary text-white' : 'bg-primary/10 text-primary hover:bg-primary/20'}`)}
        {btn('7', () => handleDigit('7'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('8', () => handleDigit('8'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('9', () => handleDigit('9'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('×', () => handleOp('×'), `${op === '×' ? 'bg-primary text-white' : 'bg-primary/10 text-primary hover:bg-primary/20'}`)}
        {btn('4', () => handleDigit('4'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('5', () => handleDigit('5'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('6', () => handleDigit('6'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('−', () => handleOp('-'), `${op === '-' ? 'bg-primary text-white' : 'bg-primary/10 text-primary hover:bg-primary/20'}`)}
        {btn('1', () => handleDigit('1'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('2', () => handleDigit('2'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('3', () => handleDigit('3'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('+', () => handleOp('+'), `${op === '+' ? 'bg-primary text-white' : 'bg-primary/10 text-primary hover:bg-primary/20'}`)}
        <button onClick={() => handleDigit('0')} className="col-span-2 h-10 rounded-xl text-sm font-semibold bg-background border border-border text-foreground hover:bg-muted transition-colors active:scale-95">0</button>
        {btn('.', handleDot, 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('=', handleEquals, 'bg-primary text-white hover:bg-primary/90')}
      </div>
    </div>);

}

export default function ProductCatalogContent() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [discountOnly, setDiscountOnly] = useState(false);
  const [selectedTax, setSelectedTax] = useState('Tous');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<typeof allProducts[0] | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showCalc, setShowCalc] = useState(false);
  const [printProduct, setPrintProduct] = useState<PrintLabelProduct | null>(null);
  const [activeTab, setActiveTab] = useState<'catalog' | 'access-codes'>('catalog');

  const filtered = useMemo(() => {
    return allProducts.filter((p) => {
      const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.barcode.includes(search);
      const matchCat = selectedCategory === 'Tous' || p.category === selectedCategory;
      const matchDiscount = !discountOnly || p.has_active_discount;
      const matchTax = selectedTax === 'Tous' ||
      selectedTax === '0%' && p.tax_rate === 0.00 ||
      selectedTax === '7%' && p.tax_rate === 0.07 ||
      selectedTax === '10%' && p.tax_rate === 0.10 ||
      selectedTax === '20%' && p.tax_rate === 0.20;
      return matchSearch && matchCat && matchDiscount && matchTax;
    });
  }, [search, selectedCategory, discountOnly, selectedTax]);

  const handleDelete = (id: string) => {setDeleteId(id);};

  const confirmDelete = () => {
    toast.success('Produit supprimé avec succès');
    setDeleteId(null);
  };

  const discountCount = allProducts.filter((p) => p.has_active_discount).length;

  return (
    <div className="space-y-5 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="text-xl font-700 text-foreground">Catalogue Produits</h2>
          <p className="text-sm text-muted-foreground">{allProducts.length} produits · {discountCount} avec remise active</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Calculator toggle */}
          <div className="relative">
            <button
              onClick={() => setShowCalc((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-600 transition-all ${showCalc ? 'bg-primary text-white border-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/40'}`}
              title="Ouvrir la calculatrice">
              
              <Calculator size={14} />
              Calculatrice
            </button>
            {showCalc &&
            <div className="absolute right-0 top-full mt-2 z-50">
                <CatalogCalculator onClose={() => setShowCalc(false)} />
              </div>
            }
          </div>
          {activeTab === 'catalog' &&
          <button onClick={() => setAddModalOpen(true)} className="btn-primary">
              <Plus size={16} />
              Ajouter Produit
            </button>
          }
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveTab('catalog')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-700 transition-all ${activeTab === 'catalog' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
          
          <Package size={13} />
          Catalogue
        </button>
        <button
          onClick={() => setActiveTab('access-codes')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-700 transition-all ${activeTab === 'access-codes' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
          
          <Key size={13} />
          Codes d'Accès
        </button>
      </div>

      {/* Access codes panel */}
      {activeTab === 'access-codes' && <CatalogAccessCodesPanel />}

      {/* Catalog content (only when catalog tab active) */}
      {activeTab === 'catalog' && <>
      {/* Filters bar */}
      <div className="card-base p-3">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
                type="text"
                placeholder="Rechercher par nom ou code-barres..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input-base pl-9" />
          </div>

          {/* Category filter */}
          <div className="flex gap-1.5 flex-wrap">
            {categories.map((cat) =>
              <button
                key={`cat-${cat}`}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-all duration-150 ${
                selectedCategory === cat ?
                'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
              {cat === 'Tous' ? 'Tous' : categoryLabels[cat] || cat}
            </button>
              )}
          </div>

          {/* Discount toggle */}
          <button
              onClick={() => setDiscountOnly(!discountOnly)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-700 transition-all duration-150 border ${
              discountOnly ?
              'bg-accent-light border-accent text-accent' : 'bg-muted border-border text-muted-foreground hover:text-foreground'}`}>
            <Tag size={13} />
            Remises Actives {discountOnly && `(${discountCount})`}
          </button>

          {/* Tax filter */}
          <div className="relative">
            <select
                value={selectedTax}
                onChange={(e) => setSelectedTax(e.target.value)}
                className="input-base pr-8 appearance-none text-xs py-1.5 min-w-[100px]">
              {taxRates.map((t) =>
                <option key={`tax-${t}`} value={t}>{t === 'Tous' ? 'TVA: Tous' : `TVA ${t}`}</option>
                )}
            </select>
            <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* View toggle */}
          <div className="flex gap-1 bg-muted rounded-lg p-1 ml-auto">
            <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-card shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
              <Grid size={15} />
            </button>
            <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-card shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
              <List size={15} />
            </button>
          </div>
        </div>

        {/* Active filters summary */}
        {(search || selectedCategory !== 'Tous' || discountOnly || selectedTax !== 'Tous') &&
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
            <span className="text-xs text-muted-foreground">{filtered.length} résultat(s)</span>
            <button
              onClick={() => {setSearch('');setSelectedCategory('Tous');setDiscountOnly(false);setSelectedTax('Tous');}}
              className="text-xs text-danger font-600 flex items-center gap-1 hover:underline">
              <X size={11} /> Effacer filtres
            </button>
          </div>
          }
      </div>

      {/* Grid view */}
      {viewMode === 'grid' &&
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
          {filtered.map((product) => {
            const stockConf = stockStatusConfig[product.stock_status];
            return (
              <div key={product.id} className="card-base p-0 overflow-hidden card-hover group">
                {/* Image */}
                <div className="relative aspect-square overflow-hidden bg-muted">
                  <AppImage
                    src={product.image}
                    alt={product.alt}
                    fill
                    sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 16vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-300" />
                  {product.has_active_discount &&
                  <div className="absolute top-2 left-2 discount-badge text-2xs px-1.5 py-0.5 rounded-full">
                      -{product.discount_pct}%
                    </div>
                  }
                  <div className="absolute top-2 right-2">
                    <span className={`text-2xs font-600 px-1.5 py-0.5 rounded-full ${stockConf.className}`}>
                      {stockConf.label}
                    </span>
                  </div>
                  {/* Hover actions */}
                  <div className="absolute inset-0 bg-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => setEditProduct(product)}
                      className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-foreground hover:bg-primary hover:text-white transition-colors"
                      title="Modifier">
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => setPrintProduct(product)}
                      className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                      title="Imprimer étiquette code-barres">
                      <Printer size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-danger hover:bg-danger hover:text-white transition-colors"
                      title="Supprimer ce produit — irréversible">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Card body */}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <p className="text-sm font-700 text-foreground leading-tight line-clamp-2">{product.name}</p>
                  </div>
                  <p className="text-2xs text-muted-foreground mb-2 font-mono">{product.barcode}</p>

                  <div className="flex items-center gap-1 mb-2">
                    <span className="text-2xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-500">
                      {categoryLabels[product.category] || product.category}
                    </span>
                    <span className="text-2xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-500">
                      TVA {(product.tax_rate * 100).toFixed(0)}%
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-base font-800 font-tabular ${product.has_active_discount ? 'text-accent' : 'text-foreground'}`}>
                        {product.wholesale_price.toFixed(2)} <span className="text-2xs font-600">MAD</span>
                      </p>
                      <p className="text-2xs text-muted-foreground font-tabular">
                        Détail: {product.retail_price.toFixed(2)} MAD
                      </p>
                    </div>
                    <span className="text-2xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-500">
                      {product.unit_type === 'Carton_Box' ? 'Carton' :
                      product.unit_type === 'Kilogram' ? 'kg' : 'Pièce'}
                    </span>
                  </div>
                </div>
              </div>);
          })}
        </div>
        }

      {/* List view */}
      {viewMode === 'list' &&
        <div className="card-base p-0 overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground w-12"></th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Produit</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Code-Barres</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Catégorie</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Prix Gros</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Prix Détail</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">TVA</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Remise</th>
                  <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Stock</th>
                  <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((product, idx) => {
                  const stockConf = stockStatusConfig[product.stock_status];
                  return (
                    <tr
                      key={product.id}
                      className={`hover:bg-muted/40 transition-colors ${idx % 2 === 0 ? '' : 'bg-muted/20'}`}>
                      <td className="px-4 py-3">
                        <div className="w-9 h-9 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                          <AppImage
                            src={product.image}
                            alt={product.alt}
                            width={36}
                            height={36}
                            className="object-cover w-full h-full" />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-600 text-foreground">{product.name}</p>
                        <p className="text-2xs text-muted-foreground">{product.unit_type === 'Carton_Box' ? 'Carton' : product.unit_type === 'Kilogram' ? 'kg' : 'Pièce'}</p>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{product.barcode}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded font-500">
                          {categoryLabels[product.category] || product.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-tabular">
                        <span className={`text-sm font-700 ${product.has_active_discount ? 'text-accent' : 'text-foreground'}`}>
                          {product.wholesale_price.toFixed(2)} MAD
                        </span>
                      </td>
                      <td className="px-4 py-3 font-tabular text-sm text-muted-foreground">
                        {product.retail_price.toFixed(2)} MAD
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground font-tabular">
                        {(product.tax_rate * 100).toFixed(0)}%
                      </td>
                      <td className="px-4 py-3">
                        {product.has_active_discount ?
                        <span className="discount-badge text-xs px-2 py-0.5 rounded-full">-{product.discount_pct}%</span> :
                        <span className="text-xs text-muted-foreground">—</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${stockConf.className}`}>
                          {stockConf.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditProduct(product)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                            title="Modifier le produit">
                            <Edit2 size={13} />
                          </button>
                          <button
                            onClick={() => setPrintProduct(product)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-blue-50 text-muted-foreground hover:text-blue-600 transition-colors"
                            title="Imprimer étiquette code-barres">
                            <Printer size={13} />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                            title="Supprimer ce produit — irréversible">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>);
                })}
              </tbody>
            </table>
          </div>
        </div>
        }

      {filtered.length === 0 &&
        <div className="card-base">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package size={32} className="text-muted-foreground mb-3" />
            <p className="text-base font-700 text-foreground mb-1">Aucun produit trouvé</p>
            <p className="text-sm text-muted-foreground mb-4">Modifiez vos filtres ou ajoutez de nouveaux produits au catalogue.</p>
            <button onClick={() => setAddModalOpen(true)} className="btn-primary">
              <Plus size={15} />
              Ajouter Produit
            </button>
          </div>
        </div>
        }

      {/* Add product modal */}
      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)} title="Ajouter un Produit" size="lg">
        <ProductForm
            onSubmit={(data) => {
              toast.success(`Produit "${data.name}" ajouté avec succès`);
              setAddModalOpen(false);
            }}
            onCancel={() => setAddModalOpen(false)} />
      </Modal>

      {/* Edit modal */}
      <Modal open={!!editProduct} onClose={() => setEditProduct(null)} title="Modifier le Produit" size="lg">
        <ProductForm
            initialData={editProduct ?? undefined}
            onSubmit={(data) => {
              toast.success(`Produit "${data.name}" mis à jour`);
              setEditProduct(null);
            }}
            onCancel={() => setEditProduct(null)} />
      </Modal>

      {/* Delete confirm modal */}
      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="Confirmer la Suppression" size="sm">
        <div className="p-5">
          <p className="text-sm text-foreground mb-1">Êtes-vous sûr de vouloir supprimer ce produit ?</p>
          <p className="text-xs text-muted-foreground mb-5">Cette action est irréversible. Toutes les données liées seront perdues.</p>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setDeleteId(null)} className="btn-secondary">Annuler</button>
            <button onClick={confirmDelete} className="btn-primary bg-danger hover:bg-red-700">
              <Trash2 size={14} />
              Supprimer
            </button>
          </div>
        </div>
      </Modal>

      {/* Print label modal */}
      {printProduct &&
        <PrintLabelModal product={printProduct} onClose={() => setPrintProduct(null)} />
        }
      </>}
    </div>);

}